import React, { useMemo, useState } from "react";
import { Formik as FormikBase } from "formik";
import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CheckIcon from "@mui/icons-material/Check";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloseIcon from "@mui/icons-material/Close";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useSelector } from "react-redux";

import LoadingButton from "@/components/loaders/LoadingButton";
import AlertBootstrapBase from "@/components/AlertBootstrap";
import RpSvgIcon from "@/components/icons/RpSvgIcon";
import { authService } from "@/features/auth/services/authService";
import { HTTP_STATUS } from "@/config/constants";
import { loggedInUserSelector } from "@/features/profile/store/userSlice";
import {
  createForcedPasswordChangeSchema,
  getForcedPasswordRuleState,
} from "@/features/auth/validators/authSchemas";

const Formik = FormikBase as unknown as React.ComponentType<any>;
const AlertBootstrap =
  AlertBootstrapBase as unknown as React.ComponentType<any>;

type PasswordRequirementItemProps = {
  inactiveColor: string;
  isValid: boolean;
  label: string;
};

const PASSWORD_REQUIREMENTS = [
  { key: "minLength", label: "At least 8 characters" },
  { key: "uppercase", label: "Include uppercase letter" },
  { key: "lowercase", label: "Include lowercase letter" },
  { key: "number", label: "Include number" },
  { key: "symbol", label: "Include symbol" },
];

const PasswordRequirementItem = ({
  isValid,
  label,
  inactiveColor,
}: PasswordRequirementItemProps) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      mb: 0.75,
      transition:
        "color 260ms ease, transform 260ms cubic-bezier(0.22, 1, 0.36, 1), opacity 220ms ease",
      transform: isValid ? "translateX(4px)" : "translateX(0)",
      opacity: isValid ? 1 : 0.85,
    }}
  >
    {isValid ? (
      <CheckIcon color="primary" className="fs-14" />
    ) : (
      <CloseIcon color={"muted" as any} className="fs-14 ml-1" />
    )}
    <Typography
      variant="body2"
      sx={{
        color: isValid ? "primary.main" : inactiveColor,
        fontWeight: 500,
      }}
    >
      {label}
    </Typography>
  </Box>
);

type PasswordFieldProps = {
  errors?: string;
  fieldLabel: string;
  name: string;
  onBlur: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onToggleVisibility: () => void;
  placeholder: string;
  showPassword: boolean;
  sx?: any;
  touched?: boolean;
  type: string;
  value: string;
};

const PasswordField = ({
  fieldLabel,
  name,
  type,
  value,
  onChange,
  onBlur,
  touched,
  errors,
  onToggleVisibility,
  showPassword,
  placeholder,
  sx,
}: PasswordFieldProps) => (
  <Box sx={{ px: { xs: 0, sm: 4 } }}>
    <Typography variant="subtitle2" className="mb-1 fw-sb">
      {fieldLabel}
    </Typography>
    <TextField
      fullWidth
      size="small"
      name={name}
      type={type}
      label=""
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      error={touched && Boolean(errors)}
      helperText={touched ? errors : ""}
      autoComplete="new-password"
      placeholder={placeholder}
      sx={sx}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton edge="end" onClick={onToggleVisibility} color="primary">
              {showPassword ? (
                <Visibility color="primary" />
              ) : (
                <VisibilityOff color="primary" />
              )}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  </Box>
);

const ForcedPasswordChangeForm = ({
  temporaryPassword = "",
  onPasswordUpdated,
}: {
  onPasswordUpdated?: (_data: unknown) => void;
  temporaryPassword?: string;
}) => {
  const theme = useTheme();
  const user = useSelector(loggedInUserSelector) as any;
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [successData, setSuccessData] = useState(null);
  const validationSchema = useMemo(
    () => createForcedPasswordChangeSchema(temporaryPassword),
    [temporaryPassword]
  );

  const onSubmit = async (values: any, { setSubmitting }: any) => {
    setSubmitError("");
    setSuccessData(null);
    const payload = {
      userId: String(user?.logedInUser ? user.logedInUser : user?.id),
      email: user?.email || "",
      password: values.password,
      confirmPassword: values.confirmPassword,
    };

    try {
      const res = await authService.changePassTempToPermanent(payload);
      if (
        (res.status === HTTP_STATUS.OK || res.status === HTTP_STATUS.CREATED) &&
        res.data
      ) {
        setSuccessData(res.data);
        onPasswordUpdated?.(res.data);
      } else {
        setSubmitError(
          res?.data?.message ||
            "Something went wrong. Please try again or contact support."
        );
      }
    } catch (error: any) {
      setSubmitError(
        error?.response?.data?.message ||
          "Something went wrong. Please try again or contact support."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={{ password: "", confirmPassword: "" }}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      validateOnChange
      validateOnBlur
      validateOnMount
    >
      {({
        values,
        errors,
        touched,
        isSubmitting,
        isValid,
        handleChange,
        handleBlur,
        handleSubmit,
      }: any) => {
        if (successData) {
          return (
            <Box
              sx={{
                px: { xs: 1.5, sm: 3 },
                py: { xs: 4, sm: 5 },
                textAlign: "center",
              }}
            >
              <CheckCircleRoundedIcon
                sx={{
                  color: "var(--status-success)",
                  fontSize: { xs: 68, sm: 76 },
                  mb: 2,
                }}
              />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  mb: 1.5,
                  color: "text.primary",
                  lineHeight: 1.2,
                }}
              >
                Password updated successfully
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.primary",
                  maxWidth: 470,
                  mx: "auto",
                  fontWeight: 400,
                }}
              >
                Your new password has been set. Please login with your new
                password.
              </Typography>
            </Box>
          );
        }

        const ruleState = getForcedPasswordRuleState(values.password);
        const inactiveRequirementColor = theme.palette.text.secondary;
        const requirementsBg = theme.palette.grey[100];
        const headingIconColor = theme.palette.primary.main;

        return (
          <form onSubmit={handleSubmit} className="px-1 px-sm-2">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                mb: 1.5,
              }}
            >
              <RpSvgIcon
                name="create_new_password"
                color={headingIconColor}
                size={35}
              />
              <Typography variant="h6" className="fw-b">
                Create Your New Password
              </Typography>
            </Box>

            <Typography variant="subtitle2" className="mb-2">
              You signed in with a temporary password. For security purposes,
              you need to create a new password before you can continue.
            </Typography>

            {submitError && (
              <AlertBootstrap
                show={submitError}
                variant="danger"
                className="mb-2 p-2 title"
                dismissible={false}
                persist={false}
                timeout={50000}
              >
                {submitError}
              </AlertBootstrap>
            )}
            <div className="p-3">
              <PasswordField
                fieldLabel="New Password"
                name="password"
                type={showPassword ? "text" : "password"}
                value={values.password}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched.password}
                errors={errors.password}
                onToggleVisibility={() => setShowPassword((prev) => !prev)}
                showPassword={showPassword}
                placeholder="Enter new password"
                sx={{ mb: 2.25, borderRadius: "var(--radius-control)" }}
              />

              <Box sx={{ px: { xs: 0, sm: 4 } }}>
                <Box
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    mb: 3,
                    borderRadius: "var(--radius-container)",
                    bgcolor: requirementsBg,
                    width: "100%",
                    maxWidth: 480,
                    mx: "auto",
                  }}
                >
                  <Typography variant="subtitle2" className="mb-1 fw-sb ml-2">
                    Password Requirements:
                  </Typography>
                  {PASSWORD_REQUIREMENTS.map((rule) => (
                    <PasswordRequirementItem
                      key={rule.key}
                      isValid={ruleState[rule.key]}
                      label={rule.label}
                      inactiveColor={inactiveRequirementColor}
                    />
                  ))}
                </Box>
              </Box>
              <PasswordField
                fieldLabel="Confirm Password"
                name="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                value={values.confirmPassword}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched.confirmPassword}
                errors={errors.confirmPassword}
                onToggleVisibility={() =>
                  setShowConfirmPassword((prev) => !prev)
                }
                placeholder="Repeat new password"
                showPassword={showConfirmPassword}
                sx={{ mb: 3 }}
              />
              <Box sx={{ px: { xs: 0, sm: 4 }, mt: 1 }}>
                <LoadingButton
                  type="submit"
                  text="Set New Password"
                  isLoading={isSubmitting}
                  disabled={!isValid || isSubmitting}
                  fullWidth
                  size="large"
                />
              </Box>
              <Typography
                variant="subtitle2"
                className="text-center mt-3 text-muted"
              >
                Your temporary password will stop working once you set a new
                one.
              </Typography>
            </div>
          </form>
        );
      }}
    </Formik>
  );
};

export default ForcedPasswordChangeForm;
